import { useFormContext } from '@oscar/form';
import { Button, Col, Input, Modal, Row, Select } from '@oscar/theme';
import { useDeepCompareEffect } from '@oscar/utils';
import classNames from 'classnames';
import { get, groupBy, isEmpty, isEqual, memoize, sortBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import './style.scss';

const { Option } = Select;

type DataType = {
  CATEGORY: {
    selectedItem: string;
    data: any[];
  };
  CONSEQUENCE: {
    name: string;
    sapConsequenceCategoryId: string;
    sapConsequenceClassId: string;
  };
  PROBABILITY: {
    name: string;
    sapProbabilityId: string;
  };
  PRIORITY: {
    sapConsequenceCategoryId: string;
    sapConsequenceClassId: string;
    sapProbabilityId: string;
    sapPriority: string;
  };
};

type ColumnType = Extract<keyof DataType, string>;
type ValueType = keyof DataType['PRIORITY'];

type ColumnItem = {
  type: ColumnType;
  data: DataType[ColumnType];
};

const getPriority = (classId: number, probabilityId: number) => {
  if (classId * probabilityId < 3) {
    return 1;
  }

  if (classId < 5 && probabilityId < 5 && classId * probabilityId <= 6) {
    return 2;
  }
  return 3;
};

const PriorityInput: React.FC<{
  value?: Record<string, any> | undefined;
  textField?: string;
  mapData?: Record<ValueType, string>;
  onChange?: (value: any) => void;
}> = ({ value, onChange, mapData }) => {
  const [visible, setVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState<Record<string, any>>();
  const handleShowModal = useCallback(() => {
    setVisible(true);
  }, []);
  const handleOk = useCallback(
    selected => {
      setVisible(false);
      if (mapData) {
        const nextValue: Record<string, any> = {};
        Object.keys(mapData).forEach(key => {
          const itemKey = key as ValueType;
          const mapKey = mapData[itemKey] || key;
          nextValue[mapKey] = get(selected, itemKey);
          onChange?.({
            ...value,
            ...nextValue,
          });
        });
      } else {
        onChange?.(selected);
      }
    },
    [currentValue],
  );

  const handleCancel = useCallback(() => {
    setVisible(false);
  }, [currentValue]);

  useDeepCompareEffect(() => {
    if (mapData) {
      const nextValue: Record<string, any> = {};
      Object.keys(mapData).forEach(key => {
        const itemKey = key as ValueType;
        nextValue[key] = get(value, mapData[itemKey] || key);
      });
      setCurrentValue(nextValue);
    } else {
      setCurrentValue(value);
    }
  }, [value]);

  const displayValue = currentValue?.sapPriority
    ? `Priority ${currentValue?.sapPriority}`
    : '';

  return (
    <>
      <Input
        placeholder="Select Priority"
        readOnly
        value={displayValue}
        onClick={handleShowModal}
      />
      {visible && (
        <PriorityModal
          selectedItem={currentValue}
          onConfirm={handleOk}
          visible={visible}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

const PriorityModal: React.FC<{
  selectedItem?: any;
  onConfirm: (item: any) => void;
  onCancel: () => void;
  visible: boolean;
}> = ({ visible, selectedItem, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState(selectedItem);
  const [formContext] = useFormContext();
  const metadata = formContext.selectors?.getDataByKey('priorities') || {};
  const [categorySelected, setCategorySelected] = useState<string>(
    selectedItem?.sapConsequenceCategoryId,
  );

  const sortedData = useMemo(() => {
    const {
      consequenceCategory,
      consequenceClass,
      probabilityClass,
      ...remain
    } = metadata;
    return {
      ...remain,
      consequenceCategory: sortBy(consequenceCategory, item => item?.order),
      consequenceClass: sortBy(consequenceClass, item => item?.order),
      probabilityClass: sortBy(probabilityClass, item => item?.order),
    };
  }, [metadata]);

  const {
    consequenceCategory,
    consequenceClass,
    consequenceDescription,
    probabilityClass,
  } = sortedData;

  useDeepCompareEffect(() => {
    if (!categorySelected && consequenceCategory.length > 0) {
      setCategorySelected(consequenceCategory?.[0]?.id);
    }
  }, [selected, consequenceCategory]);

  const categories: Record<string, any> = useMemo(() => {
    return groupBy(
      consequenceDescription,
      item => item?.sapConsequenceCategoryId,
    );
  }, [consequenceDescription]);

  const consequenceCategoryCols = useMemo(() => {
    const result: ColumnItem[] = [];
    if (!categorySelected) {
      return result;
    }
    result.push({
      type: 'CATEGORY',
      data: {
        selectedItem: categorySelected, // should be group id
        data: consequenceCategory,
      },
    });

    for (const consequence of consequenceClass) {
      const item = categories[categorySelected]?.find?.(
        (subConsequence: any) =>
          subConsequence?.sapConsequenceClassId === consequence?.id,
      );
      result.push({
        type: 'CONSEQUENCE',
        data: {
          sapConsequenceClassId: consequence?.id,
          sapConsequenceCategoryId: item?.sapConsequenceCategoryId,
          name: item?.description || 'N/A',
        },
      });
    }
    return result;
  }, [consequenceCategory, consequenceClass, categorySelected]);

  const matrixCols = useMemo(() => {
    const cols: ColumnItem[] = [];
    if (isEmpty(consequenceCategoryCols)) {
      return cols;
    }
    const getCagetoryId = memoize((classId, subConsequence) => {
      return (
        subConsequence.find(
          (item: any) => item?.data?.sapConsequenceClassId === classId,
        )?.data?.sapConsequenceCategoryId || 'Category N/A'
      );
    });

    for (let i = 0; i < probabilityClass.length; i++) {
      const probability = probabilityClass[i];
      cols.push({
        type: 'PROBABILITY',
        data: {
          name: probability?.description,
          sapProbabilityId: probability?.id,
        },
      });

      for (let j = 0; j < probabilityClass.length; j++) {
        const consequence = consequenceClass[j];
        cols.push({
          type: 'PRIORITY',
          data: {
            sapConsequenceCategoryId: getCagetoryId(
              consequence?.id,
              consequenceCategoryCols,
            ),
            sapConsequenceClassId: consequence?.id,
            sapProbabilityId: probability?.id,
            sapPriority: getPriority(
              consequence?.id,
              probability?.id,
            ).toString(),
          },
        });
      }
    }
    return cols;
  }, [consequenceClass, probabilityClass, consequenceCategoryCols]);

  return (
    <Modal
      visible={visible}
      title="Risk Assessment Matrix"
      onOk={() => onConfirm(selected)}
      okText="Confirm"
      onCancel={onCancel}
      width={800}
    >
      {!isEmpty(consequenceDescription) ? (
        <div className="priority-modal">
          <Row>
            <Col span={24}>
              <div className="consequence-title">Consequence</div>
            </Col>
          </Row>
          <Row gutter={[1, 1]} className="consequences">
            {consequenceCategoryCols.map((col, i) => {
              const baseFlex = `0 0 calc((100% - 175px)/${consequenceClass.length})`;
              const flex = ['CONSEQUENCE'].includes(col.type)
                ? baseFlex
                : '0 0 175px';
              return (
                <Col key={i.toString()} flex={flex}>
                  {col.type === 'CATEGORY' && (
                    <RendeCategory
                      {...(col?.data as DataType['CATEGORY'])}
                      onChange={select => {
                        setCategorySelected(select);
                      }}
                    />
                  )}

                  {col.type === 'CONSEQUENCE' && (
                    <div>{(col?.data as DataType['CONSEQUENCE']).name}</div>
                  )}
                </Col>
              );
            })}
          </Row>
          <Row>
            <Col span={24}>
              <div className="probability-title">Probability</div>
            </Col>
          </Row>
          <Row>
            {matrixCols.map((col, i) => {
              const baseFlex = `0 0 calc((100% - 175px)/${consequenceClass.length})`;
              const flex = ['PRIORITY'].includes(col.type)
                ? baseFlex
                : '0 0 175px';
              // const baseSpan = Math.floor(20 / consequenceClass.length);
              // const span = ['PRIORITY'].includes(col.type)
              //   ? baseSpan
              //   : 24 - consequenceClass.length * baseSpan;
              // const config =
              //   col.type === 'PROBABILITY' ? { flex: '0 0 175px' } : {};
              return (
                <Col key={i.toString()} flex={flex}>
                  {col.type === 'PRIORITY' && (
                    <Button
                      onClick={() => {
                        setSelected(col?.data);
                      }}
                      className={classNames(
                        `priority priority-${
                          (col?.data as DataType['PRIORITY']).sapPriority
                        }`,
                        { active: isEqual(col?.data, selected) },
                      )}
                    >
                      {(col?.data as DataType['PRIORITY']).sapPriority}
                    </Button>
                  )}
                  {col.type === 'PROBABILITY' && (
                    <div className="probability">
                      {(col?.data as DataType['PROBABILITY']).name}
                    </div>
                  )}
                </Col>
              );
            })}
          </Row>
        </div>
      ) : (
        <div>Can't load master data. Please check your network!</div>
      )}
    </Modal>
  );
};

const RendeCategory: React.FC<
  DataType['CATEGORY'] & { onChange: (value: string) => void }
> = ({ selectedItem, data, onChange }) => {
  return selectedItem ? (
    <Select
      className="category"
      defaultValue={selectedItem}
      onChange={select => onChange(select.toString())}
    >
      {data.map((item: any) => (
        <Option value={item?.id}>{item?.description}</Option>
      ))}
    </Select>
  ) : null;
};

export default PriorityInput;
